/**
 * Whether this business can record anything, and what to tell people first.
 *
 * The API decides — `GET /subscription/access` carries the same verdict and
 * the same words as the refusal a write would get — and this file only turns
 * that verdict into something to show. Nothing here works out for itself
 * whether a trial has ended: a second copy of that rule in the browser is a
 * second copy that can disagree with the one that actually refuses.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useAuth, useBusinessPath } from './auth';

export interface SubscriptionAccess {
  planName: string;
  status: string;
  inTrial: boolean;
  trialDaysLeft: number | null;
  trialEndsAt: string | null;
  trialExpired: boolean;
  graceUntil: string | null;
  canWrite: boolean;
  reason: 'trial_ended' | 'grace_ended' | 'expired' | 'canceled' | null;
  message: string | null;
}

/** How close to the end a trial starts being mentioned on every screen. */
export const TRIAL_WARNING_DAYS = 3;

export function useSubscriptionAccess(): SubscriptionAccess | undefined {
  const { business } = useAuth();
  const path = useBusinessPath();

  const query = useQuery({
    // Under `subscription`, so everything that refreshes the subscription
    // after a payment refreshes this with it.
    queryKey: ['subscription', business?.businessId, 'access'],
    queryFn: () => api.get<SubscriptionAccess>(path('/subscription/access')),
    enabled: Boolean(business),
    staleTime: 60 * 1000,
  });

  return query.data;
}

export interface SubscriptionNotice {
  tone: 'danger' | 'warning';
  title: string;
  body: string;
}

/**
 * The banner, if one is due.
 *
 * Nothing for a business that is fine, including a trial with weeks left: a
 * countdown on every screen from day one teaches people to ignore it before
 * it matters.
 */
export function subscriptionNotice(access: SubscriptionAccess | undefined): SubscriptionNotice | null {
  if (!access) return null;

  if (!access.canWrite) {
    return {
      tone: 'danger',
      title:
        access.reason === 'trial_ended'
          ? 'Trial ended'
          : access.reason === 'grace_ended'
            ? 'Payment overdue'
            : 'Subscription ended',
      body:
        access.message ??
        'New records cannot be saved until a plan is chosen. Existing records can still be viewed and exported.',
    };
  }

  if (
    access.inTrial &&
    access.trialDaysLeft !== null &&
    access.trialDaysLeft <= TRIAL_WARNING_DAYS
  ) {
    const days = access.trialDaysLeft;
    const when = days <= 1 ? 'within a day' : `in ${days} days`;
    return {
      tone: 'warning',
      title: `Trial ends ${when}`,
      body:
        `Your ${access.planName} trial ends ${when}. After that, new records cannot be saved ` +
        `until a plan is chosen — existing records stay viewable and exportable.`,
    };
  }

  if (access.status === 'past_due' && access.graceUntil) {
    return {
      tone: 'warning',
      title: 'Payment overdue',
      body:
        `We could not take payment. Recording continues until ` +
        `${new Date(access.graceUntil).toLocaleDateString()}; settle the invoice to keep it that way.`,
    };
  }

  return null;
}

/**
 * The status badge on the billing page.
 *
 * `trialing` is what the row says until the nightly cycle catches up, and
 * printing it next to "your trial has ended" contradicts the sentence beside
 * it. The computed `trialExpired` wins.
 */
export function subscriptionStatusBadge(sub: {
  status: string;
  trialExpired: boolean;
  /** Set while held under a CenoX internal-use grant: its review date. */
  complimentaryUntil?: string | null;
  standing?: string;
}): { label: string; tone: 'success' | 'info' | 'warning' | 'danger' } {
  if (sub.trialExpired) return { label: 'Trial ended', tone: 'danger' };
  if (sub.complimentaryUntil) {
    // Past its review date the grant has ended even though the row still
    // says active — the dates decide, as everywhere else.
    return sub.standing && sub.standing !== 'active'
      ? { label: 'Internal use ended', tone: 'danger' }
      : { label: 'Internal use', tone: 'success' };
  }
  switch (sub.status) {
    case 'active':
      return { label: 'Active', tone: 'success' };
    case 'trialing':
      return { label: 'Trial', tone: 'info' };
    case 'past_due':
      return { label: 'Payment overdue', tone: 'warning' };
    case 'canceled':
      return { label: 'Cancelled', tone: 'danger' };
    case 'expired':
      return { label: 'Expired', tone: 'danger' };
    default:
      return { label: sub.status, tone: 'danger' };
  }
}
